import { useCallback, useEffect, useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import baseUrl from '../../api/baseUrl'
import getDecryptedToken from '../../api/getDecryptedToken'
import LabOrderDetailView from '../../components/labs/LabOrderDetailView'
import SendLabOrderModal from '../../components/labs/SendLabOrderModal'
import {
  buildClinicOrderPayload,
  clinicSenderDefaults,
  emptyLabOrderValues,
  type LabOrderFormValues,
  type LabPriceListItem,
} from '../../components/labs/labOrderFormUtils'
import type { ClinicDetails } from '../../components/labs/LabOrderForm'
import {
  orderStatusStyles,
  paymentStatusStyles,
  orderMatchesKey,
  resolveOrderKey,
  type LabOrderDetail,
  type LabOrderListItem,
} from '../../components/labs/labOrderTypes'

type Partner = {
  id: string
  labId: string | null
  name: string
  address?: string
  phone?: string
  canSendOrders?: boolean
  status?: string
}

function orderErrorMessage(err: unknown) {
  const ax = err as {
    response?: { data?: unknown }
    message?: string
  }
  const data = ax?.response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object' && 'message' in data) {
    const msg = (data as { message?: string }).message
    if (msg) return msg
  }
  return ax?.message || 'Failed to send lab order'
}

function patientMetaFromRecord(p: Record<string, unknown> | undefined) {
  if (!p) return ''
  const parts: string[] = []
  const age = p.age ?? p.patientAge
  if (age != null && String(age).trim()) parts.push(`${age} yrs`)
  const gender = p.gender ?? p.sex
  if (gender != null && String(gender).trim()) parts.push(String(gender))
  return parts.join(', ')
}

function TreatmentLabOrder() {
  const { ID } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const preselectedLabId = searchParams.get('labId')
  const labsFeatureEnabled = getDecryptedToken('featureLabs') === 'true'

  const clinicDetails: ClinicDetails = {
    name: getDecryptedToken('clinicName') || 'Clinic',
    phone: getDecryptedToken('clinicPhoneNumber') || undefined,
    address: getDecryptedToken('address') || undefined,
  }

  const [partners, setPartners] = useState<Partner[]>([])
  const [values, setValues] = useState<LabOrderFormValues>(() => ({
    ...emptyLabOrderValues(),
    ...clinicSenderDefaults(clinicDetails),
  }))
  const [priceListItems, setPriceListItems] = useState<LabPriceListItem[]>([])
  const [priceListLoading, setPriceListLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [patientError, setPatientError] = useState(false)
  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [loadingPatient, setLoadingPatient] = useState(true)
  const [partnersLoading, setPartnersLoading] = useState(true)

  const [orders, setOrders] = useState<LabOrderListItem[]>([])
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [orderDetail, setOrderDetail] = useState<LabOrderDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const detailRequestRef = useRef(0)

  const loadOrders = useCallback(() => {
    if (!labsFeatureEnabled || !ID) {
      setOrders([])
      setOrdersLoading(false)
      return
    }
    setOrdersLoading(true)
    baseUrl
      .get('client/labs/orders', { params: { treatmentId: ID } })
      .then((res) => {
        const list: LabOrderListItem[] = res.data.data ?? []
        setOrders(list)
        setSelectedOrderId((prev) => {
          if (
            prev &&
            list.some(
              (o) =>
                o._id === prev ||
                o.id === prev ||
                resolveOrderKey(o) === prev,
            )
          ) {
            return prev
          }
          const first = list[0]
          return first ? resolveOrderKey(first) : null
        })
      })
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false))
  }, [ID, labsFeatureEnabled])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  useEffect(() => {
    if (!selectedOrderId || !labsFeatureEnabled) {
      setOrderDetail(null)
      return
    }
    const requestId = ++detailRequestRef.current
    const controller = new AbortController()
    setDetailLoading(true)
    setOrderDetail(null)
    baseUrl
      .get(`client/labs/orders/${encodeURIComponent(selectedOrderId)}`, {
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted || requestId !== detailRequestRef.current) return
        setOrderDetail((res.data.data ?? null) as LabOrderDetail | null)
      })
      .catch((err: { code?: string; name?: string }) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        setOrderDetail(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false)
      })
    return () => controller.abort()
  }, [selectedOrderId, labsFeatureEnabled])

  useEffect(() => {
    if (!ID) {
      setLoadingPatient(false)
      return
    }
    setLoadingPatient(true)
    baseUrl
      .get('treatment/find-treatment', { headers: { treatmentid: ID } })
      .then((response) => {
        const p = response.data.data?.patientId
        const fullName = p?.fullName?.trim() || ''
        const regNo = p?.regNo?.trim() || ''
        const label = regNo ? `${fullName} (${regNo})` : fullName
        const meta = patientMetaFromRecord(p)
        setValues((prev) => ({
          ...prev,
          patient: label,
          patientMeta:
            meta ||
            [regNo && `Reg: ${regNo}`, ID && `Treatment: ${ID}`]
              .filter(Boolean)
              .join(' · '),
          caseTitle: prev.caseTitle || (regNo ? `Case — ${regNo}` : ''),
        }))
      })
      .catch(() => {})
      .finally(() => setLoadingPatient(false))
  }, [ID])

  useEffect(() => {
    if (!labsFeatureEnabled) {
      setPartnersLoading(false)
      return
    }
    setPartnersLoading(true)
    baseUrl
      .get('client/labs/partners')
      .then((res) => {
        const list = (res.data.data ?? []).filter(
          (p: Partner) =>
            p.labId &&
            (p.canSendOrders === true ||
              (p.canSendOrders !== false && p.status === 'active')),
        )
        setPartners(list)
        const pick =
          (preselectedLabId &&
            list.find((p: Partner) => String(p.labId) === preselectedLabId)) ||
          list[0]
        if (pick?.labId) {
          setValues((prev) => ({
            ...prev,
            labId: String(pick.labId),
            labPartnerId: pick.id,
          }))
        }
      })
      .catch(() => setPartners([]))
      .finally(() => setPartnersLoading(false))
  }, [preselectedLabId, labsFeatureEnabled])

  const loadPriceList = useCallback((labId: string) => {
    if (!labId || !labsFeatureEnabled) {
      setPriceListItems([])
      return
    }
    setPriceListLoading(true)
    baseUrl
      .get('client/labs/items', { params: { labId } })
      .then((res) => setPriceListItems(res.data.data ?? []))
      .catch(() => setPriceListItems([]))
      .finally(() => setPriceListLoading(false))
  }, [labsFeatureEnabled])

  useEffect(() => {
    if (sendModalOpen) loadPriceList(values.labId)
  }, [values.labId, loadPriceList, sendModalOpen])

  useEffect(() => {
    if (values.patient.trim()) setPatientError(false)
  }, [values.patient])

  function openSendModal() {
    setError('')
    setPatientError(false)
    setSendModalOpen(true)
  }

  function closeSendModal() {
    if (submitting) return
    setSendModalOpen(false)
    setError('')
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!values.patient.trim()) {
      setPatientError(true)
      setError('Select or add a patient')
      return
    }
    setPatientError(false)
    if (!values.labId || !values.labPartnerId || !values.caseTitle.trim()) {
      setError('Select a lab and enter case details')
      return
    }
    if (!values.toothNos.length) {
      setError('Select at least one tooth')
      return
    }
    const hasLabItem = values.invoiceItems.some((row) => row.item.trim())
    if (!hasLabItem) {
      setError('Select at least one lab item')
      return
    }
    setSubmitting(true)
    setError('')
    baseUrl
      .post(
        'client/labs/orders',
        buildClinicOrderPayload(values, { treatmentId: ID }),
      )
      .then((res) => {
        const msg =
          res.data?.data?.message ||
          res.data?.message ||
          'Order sent to lab'
        toast.success(msg)
        const newId = res.data?.data?._id ?? res.data?.data?.id
        setSendModalOpen(false)
        loadOrders()
        if (newId) setSelectedOrderId(String(newId))
        setValues((prev) => ({
          ...emptyLabOrderValues(),
          ...clinicSenderDefaults(clinicDetails),
          labId: prev.labId,
          labPartnerId: prev.labPartnerId,
          patient: prev.patient,
          patientMeta: prev.patientMeta,
          caseTitle: prev.caseTitle,
        }))
      })
      .catch((err) => {
        const msg = orderErrorMessage(err)
        setError(msg)
        toast.error(msg)
      })
      .finally(() => setSubmitting(false))
  }

  if (!labsFeatureEnabled) {
    return (
      <div className="flex min-h-[calc(100dvh-11rem)] w-full items-center justify-center rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <p className="text-sm text-slate-500 text-center max-w-md">
          Lab orders are not enabled for this clinic. Contact your administrator.
        </p>
      </div>
    )
  }

  if (loadingPatient || ordersLoading) {
    return (
      <div className="flex min-h-[calc(100dvh-11rem)] w-full items-center justify-center rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    )
  }

  const partnerOptions = partners.map((p) => ({
    id: p.id,
    labId: String(p.labId),
    name: p.name,
    address: p.address,
    phone: p.phone,
  }))

  const canSend = partners.length > 0 && !partnersLoading

  return (
    <div className="flex min-h-[calc(100dvh-11rem)] w-full max-w-none flex-col gap-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Lab orders for this treatment
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Orders sent from this treatment appear below with status and full
            details.
          </p>
        </div>
        {canSend ? (
          <button
            type="button"
            onClick={openSendModal}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Send to lab
          </button>
        ) : partnersLoading ? (
          <span className="text-xs text-slate-500">Loading labs…</span>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/client/labs/partners')}
            className="text-sm font-medium text-primary hover:underline"
          >
            Add partner lab
          </button>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 px-6 py-12 text-center dark:border-slate-600">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No lab orders for this treatment yet.
          </p>
        </div>
      ) : (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {orders.length} order{orders.length === 1 ? '' : 's'}
          </h3>

          <div className="flex flex-wrap gap-2">
            {orders.map((o) => {
              const orderKey = resolveOrderKey(o)
              const active =
                selectedOrderId === o._id ||
                selectedOrderId === o.id ||
                selectedOrderId === orderKey
              return (
                <button
                  key={orderKey}
                  type="button"
                  onClick={() => {
                    setOrderDetail(null)
                    setSelectedOrderId(orderKey)
                  }}
                  className={[
                    'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                    active
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-600',
                  ].join(' ')}
                >
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    #{o.id}
                  </span>
                  <span className="mx-2 text-slate-400">·</span>
                  <span className="text-slate-600 dark:text-slate-400">
                    {o.case}
                  </span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${orderStatusStyles[o.orderStatus]}`}
                    >
                      {o.orderStatus}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${paymentStatusStyles[o.payment]}`}
                    >
                      {o.payment}
                    </span>
                    <span className="text-[10px] text-slate-500">
                      {o.labName} · {o.finalTotal}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>

          {detailLoading ? (
            <p className="text-sm text-slate-500 py-6">Loading order details…</p>
          ) : orderDetail && orderMatchesKey(orderDetail, selectedOrderId) ? (
            <LabOrderDetailView order={orderDetail} />
          ) : (
            <p className="text-sm text-slate-500 py-4">
              Could not load order details.
            </p>
          )}
        </section>
      )}

      <SendLabOrderModal
        open={sendModalOpen}
        onClose={closeSendModal}
        partners={partnerOptions}
        values={values}
        onChange={setValues}
        onSubmit={submit}
        submitting={submitting}
        error={error}
        clinicDetails={clinicDetails}
        priceListItems={priceListItems}
        priceListLoading={priceListLoading}
        patientError={patientError}
      />
    </div>
  )
}

export default TreatmentLabOrder
